import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { map, take } from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';
import { RoleDto } from '../core/mceapi.client.generated';
import { hasRole, Role } from '../shared/enums/role';

const accessMap: { [key: string]: (roles: RoleDto[]) => boolean; } = {
  '/dashboard': roles => hasRole(roles, Role.Authenticated),
  '/dashboard/license': roles => hasRole(roles, Role.Authenticated),
  '/projects': roles => hasRole(roles, Role.Authenticated),
  '/cables': roles => hasRole(roles, Role.Authenticated),
  '/account': roles => hasRole(roles, Role.Authenticated),
  '/help': roles => hasRole(roles, Role.Authenticated),
  '/administration': roles => hasRole(roles, Role.Administrator),
  '/settings': roles => hasRole(roles, Role.Authenticated),
  '/profile/edit': roles => hasRole(roles, Role.Authenticated),
  '/authentication/signin': _ => true,
  '/authentication/signin/email': _ => true,
  '/authentication/signin-complete': _ => true,
  '/authentication/signin-testing': _ => true,
};

type GuardDecision =
  | { kind: 'allow'; }
  | { kind: 'redirect'; commands: string[]; queryParams?: Record<string, string>; }
  | { kind: 'signOutAndAllow'; }
  | { kind: 'signOutAndRedirect'; commands: string[]; queryParams?: Record<string, string>; };

const isAuthenticationRoute = (url: string): boolean =>  url.startsWith('/authentication');
const isSignInCompleteRoute = (url: string): boolean =>  url === '/authentication/signin-complete' || url.startsWith('/authentication/signin-complete/');
const isProfileEditRoute = (url: string): boolean =>  url === '/profile/edit' || url.startsWith('/profile/edit/');
const isAdminRoute = (url: string): boolean =>  url === '/administration' || url.startsWith('/administration/');
const isPathMatch = (url: string, path: string): boolean =>  url === path || url.startsWith(`${path}/`);

const shouldRedirectToProfile = (url: string, isProfileComplete: boolean): boolean => {
  if (isProfileEditRoute(url) || isAuthenticationRoute(url)) return false;
  return !isProfileComplete;
};

const resolveRouteAccessCheck = (url: string) => {
  const exactMatch = accessMap[url];
  if (exactMatch) return exactMatch;

  let bestMatch: ((roles: RoleDto[]) => boolean) | undefined;
  let bestMatchLength = -1;

  for (const path in accessMap) {
    if (isPathMatch(url, path) && path.length > bestMatchLength) {
      bestMatch = accessMap[path];
      bestMatchLength = path.length;
    }
  }

  return bestMatch ?? (() => false);
};

const redirectToSignIn = (returnUrl: string): GuardDecision => ({
  kind: 'redirect',
  commands: ['/authentication/signin'],
  queryParams: { returnUrl }
});

const signOutAndRedirectToSignIn = (returnUrl: string): GuardDecision => ({
  kind: 'signOutAndRedirect',
  commands: ['/authentication/signin'],
  queryParams: { returnUrl }
});

const signOutAndAllow = (): GuardDecision => ({
  kind: 'signOutAndAllow'
});

const getGuardDecision = (url: string, fullUrl: string, authenticationService: AuthenticationService): GuardDecision => {

  const currentUser = authenticationService.currentUserState();
  const isExpired = authenticationService.isExpired();

  if (isAuthenticationRoute(url)) {
    if (isSignInCompleteRoute(url)) {
      return { kind: 'allow' };
    }

    if (!currentUser) {
      return { kind: 'allow' };
    }

    if (isExpired) {
      return signOutAndAllow();
    }

    const isProfileComplete = authenticationService.currentUserProfileComplete();

    if (!isProfileComplete) {
      return {
        kind: 'redirect',
        commands: ['/profile/edit'],
        queryParams: {
          returnUrl: fullUrl,
          reason: 'incomplete-profile'
        }
      };
    }

    return { kind: 'redirect', commands: ['/dashboard'] };
  }

  if (!currentUser) {
    return redirectToSignIn(fullUrl);
  }

  if (isExpired) {
    return signOutAndRedirectToSignIn(fullUrl);
  }

  const roles = authenticationService.currentUserRoles();

  const isProfileComplete = authenticationService.currentUserProfileComplete();

  if (shouldRedirectToProfile(url, isProfileComplete)) {
    return {
      kind: 'redirect',
      commands: ['/profile/edit'],
      queryParams: {
        returnUrl: fullUrl,
        reason: 'incomplete-profile'
      }
    };
  }

  const hasAccess = resolveRouteAccessCheck(url)(roles);

  if (hasAccess) {
    return { kind: 'allow' };
  }

  return {
    kind: 'redirect',
    commands: ['/account'],
    queryParams: { returnUrl: fullUrl }
  };
};

const decide = (
  state: RouterStateSnapshot,
  authenticationService: AuthenticationService,
  router: Router
) => {

  const url = state.url.split('?')[0];

  const decision = getGuardDecision(
    url,
    state.url,
    authenticationService
  );

  switch (decision.kind) {
    case 'allow':
      return true;

    case 'signOutAndAllow':
      authenticationService.signOut().subscribe();
      return true;

    case 'redirect':
      return router.createUrlTree(decision.commands, {
        queryParams: decision.queryParams
      });

    case 'signOutAndRedirect':
      authenticationService.signOut().subscribe();

      return router.createUrlTree(decision.commands, {
        queryParams: decision.queryParams
      });
  }
};

export const authGuard: CanActivateFn = (_, state) => {

  const authenticationService = inject(AuthenticationService);
  const router = inject(Router);

  return authenticationService.userReady$.pipe(
    take(1),
    map(() => decide(state, authenticationService, router))
  );
};
